import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import * as signalR from '@microsoft/signalr';
import { useAppSelector } from '../hooks/redux.hooks';
import { notificationsService, NotificationDto, SignalRNotificationPayload } from '../services/notifications.service';
import config from '../config';

const HUB_URL = config.NOTIFICATIONS_HUB_URL;

interface NotificationContextType {
  notifications: NotificationDto[];
  unreadCount: number;
  loading: boolean;
  connected: boolean;
  markAsRead: (id: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: number) => Promise<void>;
  deleteAllRead: () => Promise<void>;
  refreshNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, token } = useAppSelector(state => state.auth);
  const [notifications, setNotifications] = useState<NotificationDto[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const connectionRef = useRef<signalR.HubConnection | null>(null);

  const refreshNotifications = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      setLoading(true);
      const [data, count] = await Promise.all([
        notificationsService.getAll(undefined, 50),
        notificationsService.getUnreadCount(),
      ]);
      setNotifications(data);
      setUnreadCount(count);
    } catch {
      // Silently fail — notifications are non-critical
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Add incoming real-time notification to the list
  const handleIncomingNotification = useCallback((payload: SignalRNotificationPayload) => {
    const newNotif: NotificationDto = {
      notificationId: payload.notificationId,
      notificationTypeId: 0,
      userId: 0,
      title: payload.title,
      message: payload.message,
      relatedEntityType: payload.relatedEntityType,
      relatedEntityId: payload.relatedEntityId,
      isRead: false,
      priority: payload.priority,
      createdDate: payload.createdDate,
    };
    setNotifications(prev => [newNotif, ...prev]);
    setUnreadCount(prev => prev + 1);
  }, []);

  // Start SignalR connection — await previous stop before starting new
  useEffect(() => {
    let cancelled = false;

    if (!isAuthenticated || !token) {
      connectionRef.current?.stop();
      connectionRef.current = null;
      setConnected(false);
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    const startConnection = async () => {
      // Stop previous connection fully before starting a new one
      const prev = connectionRef.current;
      connectionRef.current = null;
      if (prev) {
        try { await prev.stop(); } catch { /* already stopped */ }
      }
      if (cancelled) return;

      const connection = new signalR.HubConnectionBuilder()
        .withUrl(HUB_URL, {
          accessTokenFactory: () => token,
          transport: signalR.HttpTransportType.LongPolling,
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(signalR.LogLevel.Warning)
        .build();

      connection.on('ReceiveNotification', handleIncomingNotification);
      connection.onclose(() => { if (!cancelled) setConnected(false); });
      connection.onreconnecting(() => { if (!cancelled) setConnected(false); });
      connection.onreconnected(() => {
        if (!cancelled) { setConnected(true); refreshNotifications(); }
      });

      try {
        await connection.start();
        if (!cancelled) {
          connectionRef.current = connection;
          setConnected(true);
          refreshNotifications();
        } else {
          connection.stop();
        }
      } catch {
        if (!cancelled) setConnected(false);
      }
    };

    startConnection();

    return () => {
      cancelled = true;
      connectionRef.current?.stop();
      connectionRef.current = null;
    };
  }, [isAuthenticated, token]);

  const markAsRead = useCallback(async (id: number) => {
    try {
      await notificationsService.markAsRead(id);
      setNotifications(prev =>
        prev.map(n => n.notificationId === id ? { ...n, isRead: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      // Silently ignore — non-critical
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await notificationsService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {
      // Silently ignore — non-critical
    }
  }, []);

  const deleteNotification = useCallback(async (id: number) => {
    const notif = notifications.find(n => n.notificationId === id);
    try {
      await notificationsService.delete(id);
      setNotifications(prev => prev.filter(n => n.notificationId !== id));
      if (notif && !notif.isRead) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      // Silently ignore — non-critical
    }
  }, [notifications]);

  const deleteAllRead = useCallback(async () => {
    try {
      await notificationsService.deleteAllRead();
      setNotifications(prev => prev.filter(n => !n.isRead));
    } catch {
      // Silently ignore — non-critical
    }
  }, []);

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      loading,
      connected,
      markAsRead,
      markAllAsRead,
      deleteNotification,
      deleteAllRead,
      refreshNotifications,
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
};
